import {MachineryStatus, problemCategories} from "./const";
import {problemPriority, problemStatus} from "../../../models/IMachineryProblems";
import {IMachinery} from "../../../models/iMachinery";
import {ChipStatus} from "../../../styles/theme/customizations/MyChip";
import {SelectOption} from "../../../styles/theme/customizations/MySelect";
import {t} from "../../../styles/theme/tokens";

/**
 * Минимум полей, нужных для выбора источника наработки. Структурный тип, а не
 * IMachinery: вызывается и из форм, где в руках INewMachinery без этих полей.
 */
export interface IOperatingSourceCarrier {
    operating?: number | null;
    operating_source?: "can_hours" | null;
    current_location?: { engine_total_hours_h?: number | null } | null;
}

/** Наработку машины ведёт CAN-счётчик, а не учётные часы. */
export const isOperatingByCounter = (m: IOperatingSourceCarrier): boolean =>
    m.operating_source === "can_hours";

/**
 * Наработка машины по каноническому правилу. Единственная точка этой логики
 * на фронте — фолбэков между источниками нет ни в одну сторону:
 *
 *   operating_source === 'can_hours' → current_location.engine_total_hours_h
 *                                      (данных нет → наработки нет)
 *   operating_source === null        → machinery.operating
 *
 * undefined у машины на счётчике — не «пусто», а сигнал о неисправной
 * конфигурации (счётчик не приходит). Подменять его учётными часами
 * запрещено: пользователь должен увидеть проблему, а не правдоподобное число.
 */
export const effectiveOperatingHours = (
    m: IOperatingSourceCarrier,
): number | undefined => {
    if (isOperatingByCounter(m)) {
        return m.current_location?.engine_total_hours_h ?? undefined;
    }
    return m.operating ?? undefined;
};

export const getCategoryTitleById = (id: number): string | undefined => {
    return problemCategories.find((category) => category.id === id)?.title;
};

export const getStatusTitleById = (id: number): string | undefined => {
    return problemStatus.find((status) => status.id === id)?.title;
};

export const getPriorityTitleById = (id: number): string | undefined => {
    return problemPriority.find((status) => status.id === id)?.title;
};

export const getPriorityChipColor = (priorityId: number): ChipStatus => {
    switch (priorityId) {
        case 1: // Ждёт
            return "neutral";
        case 2: // Важно
            return "warning";
        case 3: // Критично
            return "error";
        default:
            return "warning";
    }
};

export const getStatusChipColor = (status: MachineryStatus): ChipStatus => {
    switch (status) {
        case MachineryStatus.disActive:
            return "error";
        case MachineryStatus.repair:
            return "warning";
        case MachineryStatus.active:
            return "success";
        default:
            return "warning";
    }
};

const localeCompareSafe = (a: string, b: string) => {
    return a.localeCompare(b, ["ru", "en"], {sensitivity: "base"});
};
// Общая функция сортировки
export const sortMachineryList = (list: IMachinery[]): IMachinery[] => {
    return list.slice().sort((a, b) => {
        // Сортировка по type_id
        if (a.type_id !== b.type_id) return a.type_id - b.type_id;
        // По brand
        const brandCompare = localeCompareSafe(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;
        // По model
        return localeCompareSafe(a.model, b.model);
    });
};

/** Статус техники → визуальный статус чипа (для MyChip). */
export const machineryStatusToChip = (status: string): ChipStatus => {
    switch (status) {
        case MachineryStatus.active:    // "Работает"
            return "success";
        case MachineryStatus.repair:    // "В ремонте"
            return "warning";
        case MachineryStatus.disActive: // "Списана" — выведена, но не ошибка
            return "neutral";
        default:
            return "neutral";
    }
};

/** Статус техники → CSS-цвет токена (для текста borderless-селекта). */
export const machineryStatusToColor = (status: string): string => {
    switch (status) {
        case MachineryStatus.active:
            return t.success.main;
        case MachineryStatus.repair:
            return t.warning.main;
        case MachineryStatus.disActive:
            return t.text.secondary;
        default:
            return t.text.secondary;
    }
};

/** Опции для селекта смены статуса (id = строка-значение enum). */
export const MACHINERY_STATUS_OPTIONS: SelectOption[] = Object.values(MachineryStatus).map(
    (value) => ({id: value, title: value})
);

